/**
 * PasskeyList Component
 *
 * Displays a list of registered passkeys with options to:
 * - View passkey details (device name, created date, last used)
 * - Remove passkeys
 */

import { useState, useEffect, useCallback } from 'react';
import Card, { CardHeader } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Alert from '@/components/ui/Alert';
import Spinner from '@/components/ui/Spinner';
import { passkeyApi } from '@/services/passkey';
import { ApiClientError } from '@/services/api';
import { isWebAuthnSupported } from '@/utils/webauthn';
import { parseAsUTC } from '@/utils/format';
import type { Passkey } from '@/types/security';

export interface PasskeyListProps {
  className?: string;
  onAddPasskey?: () => void;
  refreshTrigger?: number;
}

/**
 * Component to display and manage registered passkeys
 */
export default function PasskeyList({
  className = '',
  onAddPasskey,
  refreshTrigger,
}: PasskeyListProps) {
  const [passkeys, setPasskeys] = useState<Passkey[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const webAuthnSupported = isWebAuthnSupported();

  // Fetch passkeys
  const fetchPasskeys = useCallback(async () => {
    if (!webAuthnSupported) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const passkeyList = await passkeyApi.listPasskeys();
      setPasskeys(passkeyList);
    } catch (err) {
      if (err instanceof ApiClientError) {
        // If 404, passkeys feature might not be enabled yet
        if (err.status === 404) {
          setPasskeys([]);
        } else {
          setError(err.detail);
        }
      } else {
        setError('Failed to load passkeys.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [webAuthnSupported]);

  useEffect(() => {
    fetchPasskeys();
  }, [fetchPasskeys, refreshTrigger]);

  // Format date for display
  // Note: Timestamps from the backend are in UTC, so we parse them as UTC.
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    const date = parseAsUTC(dateString);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Format relative time for last used
  // Note: Timestamps from the backend are in UTC, so we parse them as UTC.
  const formatRelativeTime = (dateString: string | null) => {
    if (!dateString) return 'Never used';
    const date = parseAsUTC(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      if (diffHours === 0) {
        const diffMinutes = Math.floor(diffMs / (1000 * 60));
        if (diffMinutes < 5) return 'Just now';
        return `${diffMinutes} minutes ago`;
      }
      return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    }
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} week${Math.floor(diffDays / 7) === 1 ? '' : 's'} ago`;
    return formatDate(dateString);
  };

  // Handle delete passkey
  const handleDelete = async (passkeyId: string) => {
    setDeletingId(passkeyId);
    setError(null);

    try {
      await passkeyApi.deletePasskey(passkeyId);
      setPasskeys((prev) => prev.filter((p) => p.id !== passkeyId));
      setConfirmDeleteId(null);
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.detail);
      } else {
        setError('Failed to remove passkey.');
      }
    } finally {
      setDeletingId(null);
    }
  };

  // Get device icon based on device name
  const getDeviceIcon = (deviceName: string) => {
    const name = deviceName?.toLowerCase() ?? '';
    if (name.includes('iphone') || name.includes('android') || name.includes('phone')) {
      // Phone icon
      return (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      );
    }
    if (name.includes('ipad') || name.includes('tablet')) {
      // Tablet icon
      return (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      );
    }
    if (name.includes('security key') || name.includes('yubikey')) {
      // Key icon
      return (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
        </svg>
      );
    }
    // Default computer icon
    return (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    );
  };

  // If WebAuthn is not supported, show a message
  if (!webAuthnSupported) {
    return (
      <Card className={className}>
        <CardHeader
          title="Passkeys"
          description="Sign in with biometric authentication"
        />
        <Alert variant="warning">
          Your browser does not support passkeys. Please use a modern browser to set up passkey authentication.
        </Alert>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader
        title="Passkeys"
        description="Passkeys let you sign in securely using biometrics like Face ID or Touch ID"
        action={
          onAddPasskey && (
            <Button
              variant="outline"
              size="sm"
              onClick={onAddPasskey}
              leftIcon={
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              }
            >
              Add passkey
            </Button>
          )
        }
      />

      {error && (
        <Alert variant="error" className="mb-4" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner size="md" />
        </div>
      ) : passkeys.length === 0 ? (
        <div className="text-center py-8">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 mb-4">
            <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
            </svg>
          </div>
          <h3 className="text-sm font-medium text-gray-900 mb-1">No passkeys registered</h3>
          <p className="text-sm text-gray-500 mb-4">
            Add a passkey to sign in quickly and securely with biometrics.
          </p>
          {onAddPasskey && (
            <Button variant="primary" size="sm" onClick={onAddPasskey}>
              Add your first passkey
            </Button>
          )}
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {passkeys.map((passkey) => (
            <div key={passkey.id} className="py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0 text-gray-400">
                  {getDeviceIcon(passkey.deviceName)}
                </div>
                <div>
                  <div className="font-medium text-gray-900">{passkey.deviceName}</div>
                  <div className="text-sm text-gray-500">
                    Added {formatDate(passkey.createdAt)}
                    {passkey.lastUsedAt && (
                      <span className="mx-1">&#183;</span>
                    )}
                    {passkey.lastUsedAt && (
                      <span>Last used {formatRelativeTime(passkey.lastUsedAt)}</span>
                    )}
                  </div>
                </div>
              </div>

              {confirmDeleteId === passkey.id ? (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500 mr-2">Remove?</span>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleDelete(passkey.id)}
                    isLoading={deletingId === passkey.id}
                    disabled={deletingId !== null}
                  >
                    Yes
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setConfirmDeleteId(null)}
                    disabled={deletingId !== null}
                  >
                    No
                  </Button>
                </div>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setConfirmDeleteId(passkey.id)}
                  className="text-gray-400 hover:text-red-600"
                  aria-label={`Remove passkey ${passkey.deviceName}`}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
